// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { queryOptions, useQuery } from '@tanstack/react-query';
import { getSiteFinancialSummary } from '../services/financialService';
import type { SiteFinancialSummary } from '../types';

const EMPTY_SUMMARY: SiteFinancialSummary = {};

/** Shared key/fn so every consumer of a site's KPIs lands on one cache entry. */
export function siteFinancialsQueryOptions(siteId: string | undefined) {
  return queryOptions({
    queryKey: ['site-financials', siteId ?? null] as const,
    queryFn: async () => {
      try {
        return await getSiteFinancialSummary(siteId as string);
      } catch (err) {
        // A site whose KPI listener hasn't published yet simply has no object.
        // That's an empty dashboard, not a failure worth an error banner.
        if ((err as { status?: number }).status === 404) return EMPTY_SUMMARY;
        throw err;
      }
    },
    enabled: Boolean(siteId),
  });
}

export interface UseSiteFinancialsResult {
  summary: SiteFinancialSummary;
  loading: boolean;
  error: string | null;
}

/** Month/year-to-date savings KPIs for a site, from the object-store attributes. */
export function useSiteFinancials(
  siteId: string | undefined,
  enabled = true
): UseSiteFinancialsResult {
  const options = siteFinancialsQueryOptions(siteId);
  const query = useQuery({ ...options, enabled: options.enabled && enabled });

  return {
    summary: query.data ?? EMPTY_SUMMARY,
    loading: query.isLoading && Boolean(siteId) && enabled,
    error: query.isError ? ((query.error as Error)?.message ?? 'Failed to load financials') : null,
  };
}
